/* eslint-disable relay/unused-fields */
import { notEmpty } from "@product/scmp-sdk";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type {
  liveContentLoaderQuery,
  liveContentLoaderQuery$data,
} from "~/queries/__generated__/liveContentLoaderQuery.graphql";

import type { EntityIdUuidMapEntry } from "./types";

type Payload = {
  data: liveContentLoaderQuery$data;
  entityIdUuidMap: Record<string, EntityIdUuidMapEntry>;
};

type Props = liveContentLoaderQuery["variables"] & {
  children: (payload: Payload) => void;
};

const query = graphql`
  query liveContentLoaderQuery($entityUuid: String, $randomString: String) {
    liveArticle(filter: { entityUuid: $entityUuid, randomString: $randomString }) {
      status
      liveContents {
        entityId
        entityUuid
        customTime
        ...summaryLiveArticleSummaryLiveContent
        ...timelineLiveArticleLiveContent
      }
      ...contentLiveArticleLiveArticle
      ...liveUpdateSwitchLiveArticle
    }
  }
`;

export const LiveContentLoader: FunctionComponent<Props> = ({ children, ...variables }) => {
  const data = useLazyLoadQuery<liveContentLoaderQuery>(query, variables, {
    fetchPolicy: "store-and-network",
  });

  const entityIdUuidMap = useMemo(
    () =>
      Object.fromEntries(
        data.liveArticle?.liveContents
          ?.filter(notEmpty)
          ?.filter(
            ({ customTime, entityId, entityUuid }) =>
              notEmpty(customTime) && notEmpty(entityId) && notEmpty(entityUuid),
          )
          ?.map(entry => entry as EntityIdUuidMapEntry)
          ?.map(entry => [entry.entityId, entry]) ?? [],
      ),
    [data.liveArticle?.liveContents],
  );

  return <>{children({ data, entityIdUuidMap })}</>;
};

LiveContentLoader.displayName = "LiveContentLoader";
